import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend, Cell,
} from 'recharts';
import TeamPagination from '../../TeamManager/components/TeamPagination';
import ReportingPageLayout from './ReportingPageLayout';
import KpiCardGrid from './KpiCardGrid';
import ChartTooltip from './ChartTooltip';
import ReportingTableToolbar from './ReportingTableToolbar';
import useReportingPage from '../hooks/useReportingPage';
import useSortRules from '../hooks/useSortRules';
import useTableColumns from '../hooks/useTableColumns';
import { getGranularity } from '../utils/reportingUtils';

const axios = require('axios').default;

function MarketingDetailPage({ team, csrfToken, venueOwnerships, filterOptions, config }) {
  const {
    isMobile, sidebarOpen, setSidebarOpen,
    dateRange, setDateRange,
    filterSidebarOpen, setFilterSidebarOpen,
    activeFilters, comparison,
    handleFilterChange, handleRemoveFilter, handleClearAllFilters,
  } = useReportingPage(config.defaultPreset);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 25;
  const [tableSettingsOpen, setTableSettingsOpen] = useState(false);

  const {
    sortRules, sortPopoverOpen, setSortPopoverOpen, sortableColumns,
    handleColumnSort, handleSortRuleChange, handleAddSortRule,
    handleRemoveSortRule, sortArrow,
  } = useSortRules({ columns: config.columns, defaultSort: config.defaultSort });

  const { visibleColumns, hiddenColumns, handleColumnsChange } = useTableColumns({
    columns: config.columns,
    localStorageKey: config.localStorageKey,
    teamId: team && team.id,
  });

  const filterGroups = useMemo(() => {
    if (config.buildFilterGroups) {
      return config.buildFilterGroups(venueOwnerships, filterOptions || {});
    }
    if (!venueOwnerships || venueOwnerships.length === 0) return [];
    return [{
      label: 'Venues',
      filters: [{
        id: 'venue_ownership_id',
        label: 'Venue',
        type: 'checkbox',
        options: venueOwnerships.map((vo) => ({
          value: String(vo.id),
          label: vo.name || vo.venue_name || `Venue ${vo.id}`,
        })),
      }],
    }];
  }, [venueOwnerships, filterOptions, config]);

  const buildApiParams = useCallback(() => {
    const params = {};
    if (dateRange && dateRange.start) params.start_date = dateRange.start;
    if (dateRange && dateRange.end) params.end_date = dateRange.end;
    params.granularity = getGranularity(dateRange);
    if (comparison.start) params.prior_start_date = comparison.start;
    if (comparison.end) params.prior_end_date = comparison.end;
    if (activeFilters.venue_ownership_id && activeFilters.venue_ownership_id.length > 0) {
      params['venue_ownership_id[]'] = activeFilters.venue_ownership_id;
    }
    if (config.extraApiParams) {
      Object.assign(params, config.extraApiParams(activeFilters));
    }
    return params;
  }, [dateRange, activeFilters, comparison, config]);

  useEffect(() => {
    if (!team || !team.id) return;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    setLoading(true);
    const params = buildApiParams();
    axios.get(config.apiEndpoint(team.id), { params })
      .then((res) => setData(res.data || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [team && team.id, csrfToken, dateRange, activeFilters]);

  const kpis = (data && data.kpis) || {};
  const prior = kpis.prior || {};
  const engagement = (data && data.engagement) || [];
  const rows = config.getRows ? config.getRows(data) : (data && data.messages && data.messages.rows) || [];
  const totals = config.getTotals ? config.getTotals(data) : (data && data.messages && data.messages.totals) || {};
  const cmpLabel = comparison.label;

  const sortedRows = useMemo(() => {
    const sorted = [...rows];
    sorted.sort((a, b) => {
      for (const rule of sortRules) {
        let av = a[rule.field];
        let bv = b[rule.field];
        if (typeof av === 'string') av = av.toLowerCase();
        if (typeof bv === 'string') bv = bv.toLowerCase();
        if (av == null) av = 0;
        if (bv == null) bv = 0;
        if (av < bv) return rule.direction === 'desc' ? 1 : -1;
        if (av > bv) return rule.direction === 'desc' ? -1 : 1;
      }
      return 0;
    });
    return sorted;
  }, [rows, sortRules]);

  const filteredRows = useMemo(() => {
    if (!search) return sortedRows;
    const q = search.toLowerCase();
    return sortedRows.filter((r) => config.searchFilter(r, q));
  }, [sortedRows, search]);

  useEffect(() => { setPage(1); }, [search, sortRules, rows]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / perPage));
  const paginatedRows = filteredRows.slice((page - 1) * perPage, page * perPage);

  const compactNumber = (n) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toLocaleString();
  };

  const kpiCards = config.buildKpiCards(kpis, prior, compactNumber);
  const funnelData = config.buildFunnelData ? config.buildFunnelData(kpis) : [];
  const topPerformers = config.buildTopPerformers ? config.buildTopPerformers(rows) : [];

  const handleExportCsv = () => {
    const header = visibleColumns.map((vc) => {
      const col = config.columns.find((c) => c.id === vc.id);
      return col ? col.label : vc.id;
    });
    const csvRows = sortedRows.map((r) =>
      visibleColumns.map((vc) => config.buildCsvValue ? config.buildCsvValue(vc.id, r) : '')
    );
    const csv = [header, ...csvRows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = config.csvFilename || 'export.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '\u2014';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const renderEngagementChart = () => {
    if (engagement.length === 0) {
      return (
        <div style={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--sl-text-tertiary, #667085)', fontSize: 14 }}>
          No engagement data for the selected period.
        </div>
      );
    }

    const series = config.engagementSeries;
    const yFormatter = config.engagementYFormatter;
    const tooltipEntries = series.map((s) => ({ dataKey: s.dataKey, label: s.label, format: (v) => yFormatter ? yFormatter(v) : v.toLocaleString() }));

    const sharedProps = {
      data: engagement,
      margin: { top: 5, right: 10, left: 0, bottom: 5 },
      barCategoryGap: '10%',
      barGap: 2,
    };
    const xAxisProps = {
      dataKey: 'date',
      tick: { fontSize: 12, fill: 'var(--sl-text-tertiary, #667085)' },
      axisLine: false,
      tickLine: false,
      tickFormatter: (d) => {
        const date = new Date(d + 'T00:00:00');
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      },
    };
    const yAxisProps = {
      tick: { fontSize: 12, fill: 'var(--sl-text-tertiary, #667085)' },
      axisLine: false,
      tickLine: false,
      width: yFormatter ? 40 : 50,
      ...(yFormatter ? { tickFormatter: yFormatter } : {}),
    };
    const legendProps = {
      verticalAlign: 'top',
      align: 'right',
      iconType: 'circle',
      iconSize: 8,
      wrapperStyle: { fontSize: 12, paddingBottom: 8 },
      formatter: (value) => {
        const s = series.find((s) => s.dataKey === value);
        return s ? s.label : value;
      },
    };

    return (
      <ResponsiveContainer width="100%" height={240}>
        {engagement.length <= 14 ? (
          <BarChart {...sharedProps}>
            <CartesianGrid vertical={false} stroke="#f2f4f7" />
            <XAxis {...xAxisProps} />
            <YAxis {...yAxisProps} />
            <Tooltip cursor={false} content={(props) => <ChartTooltip {...props} entries={tooltipEntries} />} />
            <Legend {...legendProps} />
            {series.map((s) => (
              <Bar key={s.dataKey} dataKey={s.dataKey} fill={s.color} radius={[4, 4, 0, 0]} barSize={40} />
            ))}
          </BarChart>
        ) : (
          <LineChart {...sharedProps}>
            <CartesianGrid vertical={false} stroke="#f2f4f7" />
            <XAxis {...xAxisProps} />
            <YAxis {...yAxisProps} />
            <Tooltip cursor={{ stroke: 'var(--sl-border-secondary, #e4e7ec)' }} content={(props) => <ChartTooltip {...props} entries={tooltipEntries} />} />
            <Legend {...legendProps} />
            {series.map((s) => (
              <Line key={s.dataKey} type="monotone" dataKey={s.dataKey} stroke={s.color} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
            ))}
          </LineChart>
        )}
      </ResponsiveContainer>
    );
  };

  const renderFunnelChart = () => {
    const hasData = config.funnelHasData ? config.funnelHasData(kpis) : funnelData.some((d) => d.value > 0);
    if (!hasData) {
      return (
        <div style={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--sl-text-tertiary, #667085)', fontSize: 14 }}>
          No data for the selected period.
        </div>
      );
    }

    const gradients = config.funnelGradients || [
      { id: 'funnelGrad0', from: 'var(--sl-fg-brand, #175cd3)', to: '#53b1fd' },
      { id: 'funnelGrad1', from: '#6941c6', to: '#b692f6' },
      { id: 'funnelGrad2', from: 'var(--sl-text-success, #027a48)', to: '#6ce9a6' },
    ];

    return (
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={funnelData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }} layout="vertical">
          <CartesianGrid horizontal={false} stroke="#f2f4f7" />
          <XAxis
            type="number"
            tick={{ fontSize: 12, fill: 'var(--sl-text-tertiary, #667085)' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => compactNumber(v)}
          />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ fontSize: 13, fill: 'var(--sl-text-secondary, #344054)', fontWeight: 500 }}
            axisLine={false}
            tickLine={false}
            width={config.funnelYAxisWidth || 60}
          />
          <Tooltip
            cursor={false}
            content={({ active, payload }) => {
              if (!active || !payload || !payload.length) return null;
              return (
                <div style={{ background: '#000', borderRadius: 8, padding: 12, minWidth: 140 }}>
                  <p style={{ color: 'var(--sl-text-white, #fff)', fontSize: 12, margin: '0 0 8px 0' }}>{payload[0].payload.name}</p>
                  <div className="d-flex align-items-center" style={{ gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: payload[0].color || 'var(--sl-fg-brand, #1982C4)', flexShrink: 0 }} />
                    <span style={{ color: '#a1a1aa', fontSize: 12 }}>{config.funnelLabel || 'Messages'}:</span>
                    <span style={{ color: 'var(--sl-text-white, #fff)', fontSize: 12, fontWeight: 600 }}>{payload[0].value.toLocaleString()}</span>
                  </div>
                </div>
              );
            }}
          />
          <defs>
            {gradients.map((g, idx) => (
              <linearGradient key={g.id || `funnelGrad${idx}`} id={g.id || `funnelGrad${idx}`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={g.from} />
                <stop offset="100%" stopColor={g.to} />
              </linearGradient>
            ))}
          </defs>
          <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={48}>
            {funnelData.map((_, index) => (
              <Cell key={`cell-${index}`} fill={`url(#${gradients[index]?.id || `funnelGrad${index}`})`} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const renderTopPerformers = () => {
    if (!config.topPerformersTitle || topPerformers.length === 0) return null;

    const series = config.topPerformersSeries || [];
    const yFormatter = config.topPerformersYFormatter || ((v) => `${v}%`);

    return (
      <div style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8, padding: '20px 24px', marginBottom: 24 }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--sl-text-primary, #101828)' }}>{config.topPerformersTitle}</span>
        <p style={{ fontSize: 13, color: 'var(--sl-text-tertiary, #667085)', marginTop: 4, marginBottom: 16 }}>{config.topPerformersSubtitle}</p>
        <ResponsiveContainer width="100%" height={Math.max(200, topPerformers.length * 36 + 40)}>
          <BarChart
            data={topPerformers.map((r) => ({
              ...r,
              name: r.name && r.name.length > 35 ? r.name.substring(0, 35) + '...' : r.name,
              fullName: r.name,
            }))}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
          >
            <CartesianGrid horizontal={false} stroke="#f2f4f7" />
            <XAxis
              type="number"
              tick={{ fontSize: 12, fill: 'var(--sl-text-tertiary, #667085)' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={yFormatter}
              domain={[0, 'auto']}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 12, fill: 'var(--sl-text-secondary, #344054)' }}
              axisLine={false}
              tickLine={false}
              width={180}
            />
            <Tooltip
              cursor={false}
              content={({ active, payload }) => {
                if (!active || !payload || !payload.length) return null;
                const fullName = payload[0].payload.fullName;
                return (
                  <div style={{ background: '#000', borderRadius: 8, padding: 12, minWidth: 140 }}>
                    <p style={{ color: 'var(--sl-text-white, #fff)', fontSize: 12, margin: '0 0 8px 0' }}>{fullName}</p>
                    {payload.map((entry, idx) => {
                      const s = series.find((s) => s.dataKey === entry.dataKey);
                      return (
                        <div key={idx} className="d-flex align-items-center" style={{ gap: 6, marginBottom: 4 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: entry.color, flexShrink: 0 }} />
                          <span style={{ color: '#a1a1aa', fontSize: 12 }}>{s ? s.label : entry.dataKey}:</span>
                          <span style={{ color: 'var(--sl-text-white, #fff)', fontSize: 12, fontWeight: 600 }}>{yFormatter(entry.value)}</span>
                        </div>
                      );
                    })}
                  </div>
                );
              }}
            />
            <Legend
              verticalAlign="top"
              align="right"
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 12, paddingBottom: 8 }}
              formatter={(value) => {
                const s = series.find((s) => s.dataKey === value);
                return s ? s.label : value;
              }}
            />
            {series.map((s) => (
              <Bar key={s.dataKey} dataKey={s.dataKey} fill={s.color} radius={[0, 4, 4, 0]} maxBarSize={20} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderSecondChart = () => {
    if (config.renderSecondChart) {
      return config.renderSecondChart({ data, loading, compactNumber, isMobile });
    }
    return (
      <div style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8, padding: '20px 24px' }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--sl-text-primary, #101828)' }}>{config.secondChartTitle}</span>
        <p style={{ fontSize: 13, color: 'var(--sl-text-tertiary, #667085)', marginTop: 4, marginBottom: 16 }}>{config.secondChartSubtitle}</p>
        {renderFunnelChart()}
      </div>
    );
  };

  return (
    <ReportingPageLayout
      team={team}
      activeKey={config.sidebarKey}
      title={config.pageTitle}
      subtitle={config.pageSubtitle}
      dateRange={dateRange}
      setDateRange={setDateRange}
      isMobile={isMobile}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      filterSidebarOpen={filterSidebarOpen}
      setFilterSidebarOpen={setFilterSidebarOpen}
      activeFilters={activeFilters}
      filterGroups={filterGroups}
      handleFilterChange={handleFilterChange}
      handleRemoveFilter={handleRemoveFilter}
      handleClearAllFilters={handleClearAllFilters}
      filterTitle={config.filterTitle}
      filterSubtitle={config.filterSubtitle}
    >
      <KpiCardGrid
        loading={loading}
        isMobile={isMobile}
        columns={config.kpiColumns}
        cards={kpiCards}
        comparisonLabel={cmpLabel}
        variant="centered"
      />

      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 24 }}>
          <div style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8, padding: '20px 24px' }}>
            <div style={{ width: 200, height: 18, background: 'var(--sl-bg-tertiary, #f2f4f7)', borderRadius: 4, marginBottom: 20 }} />
            <div style={{ width: '100%', height: 240, background: 'var(--sl-bg-secondary, #f9fafb)', borderRadius: 4 }} />
          </div>
          <div style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8, padding: '20px 24px' }}>
            <div style={{ width: 200, height: 18, background: 'var(--sl-bg-tertiary, #f2f4f7)', borderRadius: 4, marginBottom: 20 }} />
            <div style={{ width: '100%', height: 240, background: 'var(--sl-bg-secondary, #f9fafb)', borderRadius: 4 }} />
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 24 }}>
          <div style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8, padding: '20px 24px' }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--sl-text-primary, #101828)' }}>{config.engagementTitle}</span>
            <p style={{ fontSize: 13, color: 'var(--sl-text-tertiary, #667085)', marginTop: 4, marginBottom: 16 }}>{config.engagementSubtitle}</p>
            {renderEngagementChart()}
          </div>
          {renderSecondChart()}
        </div>
      )}

      {loading ? (
        config.topPerformersTitle && (
          <div style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8, padding: '20px 24px', marginBottom: 24 }}>
            <div style={{ width: 200, height: 18, background: 'var(--sl-bg-tertiary, #f2f4f7)', borderRadius: 4, marginBottom: 20 }} />
            <div style={{ width: '100%', height: 280, background: 'var(--sl-bg-secondary, #f9fafb)', borderRadius: 4 }} />
          </div>
        )
      ) : renderTopPerformers()}

      {config.renderExtraCharts && !loading && config.renderExtraCharts({ data, isMobile })}

      <div style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8 }}>
        <ReportingTableToolbar
          title={config.tableTitle}
          onExport={handleExportCsv}
          tableSettingsOpen={tableSettingsOpen}
          setTableSettingsOpen={setTableSettingsOpen}
          visibleColumns={visibleColumns}
          hiddenColumns={hiddenColumns}
          onColumnsChange={handleColumnsChange}
          sortRules={sortRules}
          sortPopoverOpen={sortPopoverOpen}
          setSortPopoverOpen={setSortPopoverOpen}
          sortableColumns={sortableColumns}
          sortTypes={config.sortTypes}
          onSortRuleChange={handleSortRuleChange}
          onAddSortRule={handleAddSortRule}
          onRemoveSortRule={handleRemoveSortRule}
          isMobile={isMobile}
        />

        <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--sl-bg-tertiary, #f2f4f7)' }}>
          <div style={{ position: 'relative', maxWidth: 400 }}>
            <i className="fas fa-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--sl-text-disabled, #98a2b3)', fontSize: 14 }} />
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder={config.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: 36, border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8, height: 40 }}
            />
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr>
                {visibleColumns.map((vc) => {
                  const col = config.columns.find((c) => c.id === vc.id);
                  if (!col) return null;
                  return (
                    <th
                      key={col.id}
                      style={{
                        fontWeight: 500, fontSize: 12, color: 'var(--sl-text-tertiary, #667085)', textTransform: 'uppercase',
                        textAlign: col.align,
                        ...(col.sortable ? { cursor: 'pointer', userSelect: 'none' } : {}),
                        whiteSpace: 'nowrap',
                        ...(col.first ? { paddingLeft: 24 } : {}),
                      }}
                      onClick={col.sortable ? () => handleColumnSort(col.id) : undefined}
                    >
                      {col.label}{sortArrow(col.id)}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={visibleColumns.length} className="text-center py-4 text-muted">Loading...</td></tr>
              ) : paginatedRows.length === 0 ? (
                <tr><td colSpan={visibleColumns.length} className="text-center py-4 text-muted">{search ? config.emptySearchMessage || 'No messages match your search.' : config.emptyMessage}</td></tr>
              ) : paginatedRows.map((row) => (
                <tr key={row.id}>
                  {visibleColumns.map((vc) => {
                    const col = config.columns.find((c) => c.id === vc.id);
                    if (!col) return null;
                    return (
                      <td key={col.id} style={{
                        fontSize: 14,
                        color: col.id === config.nameColumn ? 'var(--sl-text-primary, #101828)' : 'var(--sl-text-secondary, #344054)',
                        fontWeight: col.id === config.nameColumn ? 600 : undefined,
                        textAlign: col.align,
                        whiteSpace: col.id === 'created_at' ? 'nowrap' : undefined,
                        ...(col.first ? { paddingLeft: 24 } : {}),
                      }}>
                        {config.renderCellValue(col.id, row, formatDate)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            {filteredRows.length > 0 && config.buildTotals && (
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--sl-border-secondary, #e4e7ec)' }}>
                  {visibleColumns.map((vc, i) => {
                    const col = config.columns.find((c) => c.id === vc.id);
                    if (!col) return null;
                    const totalMap = config.buildTotals(totals);
                    if (totalMap[col.id] !== undefined) {
                      return (
                        <td key={col.id} style={{ textAlign: 'right', fontWeight: 700, color: 'var(--sl-text-primary, #101828)' }}>
                          {totalMap[col.id]}
                        </td>
                      );
                    }
                    const hasLaterTotal = visibleColumns.slice(i + 1).some((v) => totalMap[v.id] !== undefined);
                    if (!hasLaterTotal) {
                      return (
                        <td key={col.id} style={{ paddingLeft: col.first ? 24 : undefined, fontWeight: 700, color: 'var(--sl-text-primary, #101828)', textAlign: 'right' }}>
                          Grand total
                        </td>
                      );
                    }
                    return <td key={col.id} />;
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {totalPages > 1 && (
          <div style={{ padding: '12px 24px', borderTop: '1px solid var(--sl-bg-tertiary, #f2f4f7)' }}>
            <TeamPagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={filteredRows.length}
              pageSize={perPage}
              itemLabel={config.paginationLabel}
              onPageChange={(p) => { if (p >= 1 && p <= totalPages) setPage(p); }}
            />
          </div>
        )}
      </div>
    </ReportingPageLayout>
  );
}

export default MarketingDetailPage;
